<template>
    <rkm-modal
        :title="`Configuração de especialidade - ${item && item.unidadeNomeFantasia}`"
        extra-large
        ref="RkmModal"
    >
        <rkm-form
            ref="RkmForm"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="8">
                    <rkm-form-input-autocomplete
                        :disabled="editing"
                        :repository="repositories.especialidade"
                        :request-query-string-params="requestQueryStringParams"
                        :search-fields="searchFields"
                        :text-field="(item) => item.ocupacaoCodigo ? `${item.ocupacaoCodigo} - ${item.nome}` : null"
                        :validations="validations.especialidade"
                        @select="handleEspecialidadeSelect"
                        label="Especialidade"
                        v-model="model.especialidade"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input
                        :validations="validations.avisaFaltaQuantidade"
                        label="Quantidade de faltas para aviso"
                        number
                        v-model="model.avisaFaltaQuantidade"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-input
                        :disabled="!model.avisaFaltaQuantidade"
                        :validations="validations.avisaFaltaDias"
                        label="Dias de antecedência para aviso"
                        number
                        v-model="model.avisaFaltaDias"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        :validations="validations.quantidadeDiasRetorno"
                        class="mb-0"
                        label="Quantidade de dias para retorno"
                        number
                        v-model="model.quantidadeDiasRetorno"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="!model.especialidade || (model.especialidade && !!model.especialidade.isRegulado)"
                        label="Observa vagas de atendimento"
                        tooltip-right="Quando ativo, a demanda espontânea passa a respeitar limites de agenda por profissional/setor e pode exigir liberação por meio de senha de supervisor"
                        v-model="model.demandaEspontaneaObservaVagaAtendimento"
                    >
                        <rkm-form-select-option :value="true">Sim</rkm-form-select-option>
                        <rkm-form-select-option :value="false">Não</rkm-form-select-option>
                    </rkm-form-select>
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :label="false"
                        @click="include"
                        color="primary"
                    >
                        {{ editing ? 'Salvar' : 'Incluir' }}
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click="clean"
                    >
                        {{ editing ? 'Cancelar' : 'Limpar' }}
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-row>
            <rkm-col>
                <rkm-table
                    :fields="fields"
                    :items="model.especialidades"
                >
                    <template v-slot:cell(especialidade)="{item}">
                        {{item.ocupacaoCodigo}} - {{item.nome}}
                    </template>

                    <template v-slot:cell(actions)="{index, item}">
                        <rkm-button
                            @click="$refs.ModalProcedimento.open(item)"
                            color="primary"
                            small
                            title="Procedimentos padrões"
                        >
                            <rkm-icon icon="bars" />
                        </rkm-button>

                        <rkm-button
                            @click="edit(item)"
                            color="primary"
                            small
                            title="Editar"
                        >
                            <rkm-icon icon="pen" />
                        </rkm-button>

                        <rkm-button
                            @click="destroy(index)"
                            color="danger"
                            small
                            title="Excluir"
                        >
                            <rkm-icon icon="trash" />
                        </rkm-button>
                    </template>
                </rkm-table>
            </rkm-col>
        </rkm-row>

        <template v-slot:footer="{ cancel }">
            <rkm-button @click="cancel">
                Cancelar
            </rkm-button>

            <rkm-button
                @click="save"
                color="primary"
            >
                Salvar
            </rkm-button>
        </template>

        <ModalProcedimento ref="ModalProcedimento" />
    </rkm-modal>
</template>

<script>
// Components.
import ModalProcedimento from './components/procedimento/Modal'

// Mixins.
import {error, object, repository, stringCase} from '@/mixins'

export default {
    components: {ModalProcedimento},
    computed: {
        requestQueryStringParams() {
            return this.item
                ? {ocupacoes: this.item.ocupacoes.map(ocupacao => ocupacao.id)}
                : null
        },
        searchFields() {
            return [
                {key: 'especialidade.nome', searchable: true},
            ]
        },
        validations() {
            return {
                especialidade: {required: true},
                avisaFaltaQuantidade: {max: 3},
                avisaFaltaDias: {
                    required: !!this.model.avisaFaltaQuantidade,
                    max: 3,
                },
                quantidadeDiasRetorno: {max: 3},
            }
        },
    },
    created() {
        this.repositories = {
            especialidade: this.getConectaSusRepository('cadastro.especialidade'),
        }
    },
    data: () => ({
        editing: false,
        fields: [
            {key: 'especialidade', label: 'Especialidade', sortable: true},
            {key: 'avisaFaltaQuantidade', label: 'Quantidade de faltas para aviso', sortable: true},
            {key: 'avisaFaltaDias', label: 'Dias de antecedência para aviso', sortable: true},
            {key: 'quantidadeDiasRetorno', label: 'Quantidade de dias para retorno', sortable: true},
            {formatter: (value) => value ? 'Sim' : 'Não', key: 'demandaEspontaneaObservaVagaAtendimento', label: 'Observa vagas de atendimento', sortable: true},
            {class: 'col-min text-center', key: 'actions', label: 'Ações'},
        ],
        item: null,
        model: {
            especialidade: null,
            avisaFaltaQuantidade: null,
            avisaFaltaDias: null,
            quantidadeDiasRetorno: null,
            demandaEspontaneaObservaVagaAtendimento: false,
            especialidades: [],
        },
    }),
    methods: {
        open(item) {
            this.resetForm()

            this.item = item

            this.model.especialidades = this.clone(item.especialidades)

            this.$refs.RkmModal.show()
        },
        store() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (this.model.especialidades && this.model.especialidades.find(especialidade => especialidade.id === this.model.especialidade.id)) {
                        this.showValidationNotification('A especialidade já foi incluída.')
                        return
                    }

                    this.model.especialidades.push({
                        id: this.model.especialidade.id,
                        nome: this.model.especialidade.nome,
                        ocupacaoId: this.model.especialidade.ocupacaoId,
                        ocupacaoCodigo: this.model.especialidade.ocupacaoCodigo,
                        avisaFaltaQuantidade: this.model.avisaFaltaQuantidade,
                        avisaFaltaDias: this.model.avisaFaltaDias,
                        quantidadeDiasRetorno: this.model.quantidadeDiasRetorno,
                        demandaEspontaneaObservaVagaAtendimento: this.model.demandaEspontaneaObservaVagaAtendimento,
                        isRegulado: this.model.isRegulado,
                        procedimentos: [],
                    })

                    this.resetForm()
                })
        },
        edit(item) {
            this.resetForm()

            this.editing = true

            this.model.especialidade = {
                id: item.id,
                nome: item.nome,
                ocupacaoId: item.ocupacaoId,
                ocupacaoCodigo: item.ocupacaoCodigo,
                isRegulado: item.isRegulado,
            }

            this.model.avisaFaltaQuantidade = item.avisaFaltaQuantidade
            this.model.avisaFaltaDias = item.avisaFaltaDias
            this.model.quantidadeDiasRetorno = item.quantidadeDiasRetorno
            this.model.demandaEspontaneaObservaVagaAtendimento = item.demandaEspontaneaObservaVagaAtendimento
        },
        update() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    const especialidade = this.model.especialidades.find(especialidade => especialidade.id === this.model.especialidade.id)

                    especialidade.avisaFaltaQuantidade = this.model.avisaFaltaQuantidade
                    especialidade.avisaFaltaDias = this.model.avisaFaltaDias
                    especialidade.quantidadeDiasRetorno = this.model.quantidadeDiasRetorno
                    especialidade.demandaEspontaneaObservaVagaAtendimento = this.model.demandaEspontaneaObservaVagaAtendimento

                    this.resetForm()
                })
        },
        include() {
            if (this.editing) {
                this.update()
            } else {
                this.store()
            }
        },
        destroy(index) {
            this.showDeleteConfirmation('Deseja realmente excluir a especialidade?', async () => {
                this.model.especialidades.splice(index, 1)
            })
        },
        save() {
            this.item.especialidades = this.model.especialidades

            this.$refs.RkmModal.hide()
        },
        clean() {
            this.resetForm()
        },
        resetForm() {
            this.editing = false
            this.model.especialidade = null
            this.model.avisaFaltaQuantidade = null
            this.model.avisaFaltaDias = null
            this.model.quantidadeDiasRetorno = null
            this.model.demandaEspontaneaObservaVagaAtendimento = false

            this.$nextTick().then(() => this.$refs.RkmForm.resetValidator())
        },
        handleEspecialidadeSelect() {
            this.model.demandaEspontaneaObservaVagaAtendimento = false
        },
    },
    mixins: [error, object, repository, stringCase],
    watch: {
        'model.avisaFaltaQuantidade'(value) {
            if (value === null) {
                this.model.avisaFaltaDias = null
            }
        },
    },
}
</script>